import * as z from "zod";
import { pagination } from "./pagination";

// Search across the records of a domain (ADR-117): every domain answers for its own types, with the same input and
// the same hit, and the app asks the domains side by side. A hit names its entity, never a route: the app decides
// where it opens.

/**
 * How a hit answers the query, from its score (bands of `app.search_score`): the whole code or text, its start or
 * the start of a word, a part of it, or only a similar word (a typo).
 */
export const searchMatchKinds = ["exact", "prefix", "contains", "similar"] as const;
export type SearchMatchKind = (typeof searchMatchKinds)[number];

/**
 * The score of a code equal to the text asked, the top band of `app.search_score` (a text equal to it scores less): the
 * only hit an app offers as the best match.
 */
export const EXACT_CODE_SCORE = 1;

/** What a domain is asked: the text, one of its types or all of them, the page of each type. */
export function searchInputOf<const T extends readonly [string, ...string[]]>(types: T) {
  return z.object({
    q: z.string().trim().min(2).max(100),
    type: z.enum(types).optional(),
    ...pagination({ defaultSize: 5, maxSize: 25 }),
  });
}

/** One entity found. `title` never hides: it is the first field of the entity the Actor can see. */
export function searchHitOf<const T extends readonly [string, ...string[]]>(types: T) {
  return z.object({
    type: z.enum(types),
    id: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    /** The code people know the entity by (a style code, a PO number), when the Actor can see it. */
    code: z.string().nullable(),
    /** The entity a hit opens in (the product of a SKU), when it has no place of its own. */
    parentId: z.string().nullable(),
    archived: z.boolean(),
    /** The field (of the entity's DTO) that answered best, and its value. */
    matched: z.object({ field: z.string(), value: z.string(), kind: z.enum(searchMatchKinds) }),
    score: z.number().min(0).max(1),
  });
}

/** The answer of a domain: one page per type asked, best first. */
export function searchResultOf<const T extends readonly [string, ...string[]]>(types: T) {
  return z.object({
    groups: z.array(
      z.object({
        type: z.enum(types),
        page: z.number().int(),
        pageSize: z.number().int(),
        hasMore: z.boolean(),
        items: z.array(searchHitOf(types)),
      }),
    ),
  });
}
